import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import {
  Alert,
  AlertTitle,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FieldWrapperVertical,
  TextField,
  useDataProvider,
  useLocalization,
  useMasterData,
  useSnackbar,
  LoadingEvent,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import DialogCloseButton from './DialogCloseButton';
import IconLibrary from '../IconLibrary';

function UpdatePasswordDialog({ open, onClose, userEmail }) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const masterData = useMasterData();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post } = useDataProvider(provider);

  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [formErrors, setFormErrors] = useState([]);

  const keycloakPasswordPolicy = masterData?.keycloakPasswordPolicy;

  const schema = yup.object().shape({
    newPassword: yup.string().required(
      formatMessage({
        id: 'REQUIRED',
        defaultMessage: 'Required',
      }),
    ),
    retypePassword: yup
      .string()
      .required(
        formatMessage({
          id: 'REQUIRED',
          defaultMessage: 'Required',
        }),
      )
      .oneOf(
        [yup.ref('newPassword')],
        formatMessage({
          id: 'PASSWORDS_MUST_MATCH',
          defaultMessage: 'Passwords must match',
        }),
      ),
  });

  const methods = useForm({ resolver: yupResolver(schema) });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  async function onFormSubmit(data) {
    setLoading(true);
    setFormErrors([]);
    const response = await post(`${getApiUrl()}/sxcl/v1/auth/reset-password`, {
      email: userEmail,
      password: data?.newPassword,
    });
    if (response.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'PASSWORD_CHANGED_SUCCESSFULLY',
          defaultMessage: 'Password changed successfully',
        }),
      );
      onClose();
    } else {
      setFormErrors(response?.errors || []);
    }
    setLoading(false);
  }

  const buttons = [
    {
      children: formatMessage({
        id: 'CHANGE_PASSWORD',
        defaultMessage: 'Change Password',
      }),
      variant: 'contained',
      color: 'primary',
      onClick: () => {
        handleSubmit(onFormSubmit)();
      },
      startIcon: <IconLibrary.Password />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: onClose,
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  function renderRequirement(key, value) {
    switch (key) {
      case 'minLength':
        return formatMessage({
          id: 'PASSWORD_MIN_LENGTH',
          defaultMessage: `Minimum of ${value} characters`,
        });
      case 'maxLength':
        return formatMessage({
          id: 'PASSWORD_MAX_LENGTH',
          defaultMessage: `Maximum of ${value} characters`,
        });
      case 'minUpperCase':
        return formatMessage({
          id: 'PASSWORD_UPPERCASE',
          defaultMessage: `Contains at least ${value} uppercase letter${
            value === '1' ? '' : 's'
          }`,
        });
      case 'minLowerCase':
        return formatMessage({
          id: 'PASSWORD_LOWERCASE',
          defaultMessage: `Contains at least ${value} lowercase letter${
            value === '1' ? '' : 's'
          }`,
        });
      case 'minDigits':
        return formatMessage({
          id: 'PASSWORD_NUMBER',
          defaultMessage: `Contains at least ${value} digit${
            value === '1' ? '' : 's'
          }`,
        });
      case 'minSpecialChars':
        return formatMessage({
          id: 'PASSWORD_NUMBER',
          defaultMessage: `Contains at least ${value} special characters${
            value === '1' ? '' : 's'
          }`,
        });
      default:
        return '';
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.UpdatePassword />
        {formatMessage({
          id: 'UPDATE_PASSWORD',
          defaultMessage: 'Update Password',
        })}
        <DialogCloseButton disabled={loading} onClose={onClose} />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {!loading && formErrors && formErrors.length > 0 && (
              <Alert sx={{ mb: 2, mt: -1 }} severity="error">
                <AlertTitle>
                  {formatMessage({
                    id: 'FORM_ERRORS',
                    defaultMessage: 'Form Errors',
                  })}
                </AlertTitle>
                <ul>
                  {formErrors.map(actionError => (
                    <li key={actionError?.message}>{actionError?.message}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {loading ? (
              <Box>
                <LoadingEvent>
                  <FormattedMessage
                    id="PROCESSING"
                    defaultMessage="Processing"
                  />
                  ...
                </LoadingEvent>
              </Box>
            ) : (
              <Grid2 container spacing={2}>
                <Grid2 xs={12}>
                  <FieldWrapperVertical
                    field={{
                      name: 'newPassword',
                      label: {
                        localeId: 'NEW_PASSWORD',
                        defaultMessage: 'New Password',
                      },
                    }}
                    errors={errors}>
                    <TextField
                      type={showNewPassword ? 'text' : 'password'}
                      fullWidth
                      {...methods.register('newPassword')}
                      error={!!errors?.newPassword?.message}
                      helperText={errors?.newPassword?.message || ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              onClick={() => setShowNewPassword(prev => !prev)}
                              edge="end">
                              {showNewPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FieldWrapperVertical>
                </Grid2>
                <Grid2 xs={12}>
                  <FieldWrapperVertical
                    field={{
                      name: 'retypePassword',
                      label: {
                        localeId: 'RETYPE_PASSWORD',
                        defaultMessage: 'Retype Password',
                      },
                    }}
                    errors={errors}>
                    <TextField
                      type={showRetypePassword ? 'text' : 'password'}
                      fullWidth
                      {...methods.register('retypePassword')}
                      error={!!errors?.retypePassword?.message}
                      helperText={errors?.retypePassword?.message || ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              onClick={() =>
                                setShowRetypePassword(prev => !prev)
                              }
                              edge="end">
                              {showRetypePassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FieldWrapperVertical>
                </Grid2>
              </Grid2>
            )}
            {!loading && (
              <Alert icon={false} severity="info" sx={{ m: 1 }}>
                <AlertTitle>
                  {formatMessage({
                    id: 'PASSWORD_REQUIREMENTS',
                    defaultMessage: 'Password Requirements',
                  })}
                </AlertTitle>
                <ul
                  style={{
                    fontSize: '12px',
                    marginTop: 0,
                    marginBottom: 0,
                    paddingLeft: '20px',
                  }}>
                  {keycloakPasswordPolicy &&
                    Object.entries(keycloakPasswordPolicy).map(
                      ([key, value]) => {
                        const req = renderRequirement(key, value);
                        return req ? <li key={key}>{req}</li> : null;
                      },
                    )}
                </ul>
              </Alert>
            )}
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            {buttons.map(btn => {
              const { children, ...rest } = btn;
              return (
                <Button
                  type="button"
                  key={children}
                  disabled={loading}
                  {...rest}>
                  {children}
                </Button>
              );
            })}
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default UpdatePasswordDialog;
